import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { useLanguage } from "../context/LanguageContext";
import ProductCard from "../components/ProductCard";
import { SEO, OrganizationSchema } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const { t } = useLanguage();
  const [featured, setFeatured] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [prodRes, brandRes] = await Promise.all([
          axios.get(`${API}/products?featured=true&limit=4`),
          axios.get(`${API}/brands`),
        ]);
        setFeatured(prodRes.data.products || []);
        setBrands(brandRes.data.map((b) => (typeof b === "string" ? b : b.name)));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        title="Authentic Pre-Owned Designer Handbags"
        description="Shop authentic pre-owned luxury designer handbags from Chanel, Louis Vuitton, Hermès, Gucci and more at Mona Luxe. Free authentication & 14-day returns."
        path="/"
      />
      <OrganizationSchema />
      {/* Hero */}
      <section className="relative h-[60vh] md:h-[90vh] overflow-hidden bg-[#0F0F0F]" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/files/mona-luxe/hero/hero-6f9dbabf-a0c8-4726-a5a7-52380f387ae5.jpg`}
            alt="Mona Luxe — worn, loved, next"
            className="w-full h-full object-cover object-[78%_center] md:object-center"
          />
          {/* Bold gradient — dark on the left for text legibility, transparent on the right so the accent color pops */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-transparent md:from-black/75 md:via-black/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        </div>
        <div className="relative h-full flex items-center pt-4 pb-10 md:pt-0 md:pb-0 px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="max-w-2xl text-white"
          >
            <p className="text-[10px] uppercase tracking-[0.35em] text-white/60 mt-4 md:mt-0 mb-4 md:mb-6" data-testid="hero-eyebrow">
              {t("hero.eyebrow")}
            </p>
            <h1
              className="text-5xl sm:text-6xl md:text-7xl lg:text-[8rem] leading-[0.9] mb-4 md:mb-8 uppercase font-medium"
              style={{ fontFamily: "'Playfair Display', serif", letterSpacing: "-0.02em" }}
              data-testid="hero-title"
            >
              WORN.<br />LOVED.<br />NEXT.
            </h1>
            <p className="text-sm md:text-base text-white/75 mb-10 tracking-wide leading-relaxed max-w-md" data-testid="hero-subtitle">
              {t("hero.subtitle")}
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 text-xs uppercase tracking-[0.25em] font-medium hover:bg-white/90 transition-all"
              data-testid="hero-cta"
            >
              {t("hero.cta")}
              <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32" data-testid="featured-section">
        <div className="flex items-end justify-between mb-16">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-3">
              {t("home.featured")}
            </p>
            <h2
              className="text-2xl md:text-3xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("home.featured_subtitle")}
            </h2>
          </div>
          <Link
            to="/shop"
            className="hidden md:flex items-center gap-2 text-xs uppercase tracking-[0.12em] hover:opacity-70 transition-opacity"
            data-testid="view-all-link"
          >
            {t("home.view_all")}
            <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] bg-[#F4F0EB]" />
                <div className="mt-4 space-y-2">
                  <div className="h-2 bg-[#F4F0EB] w-16" />
                  <div className="h-3 bg-[#F4F0EB] w-32" />
                  <div className="h-2 bg-[#F4F0EB] w-20" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {featured.map((product, i) => (
              <ProductCard key={product.id} product={product} index={i} />
            ))}
          </div>
        )}

        <div className="md:hidden mt-8 text-center">
          <Link
            to="/shop"
            className="btn-luxury-outline inline-flex items-center gap-2"
            data-testid="view-all-link-mobile"
          >
            {t("home.view_all")}
            <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      {/* Brand Showcase */}
      <section className="bg-[#F4F0EB] px-6 md:px-12 lg:px-24 py-24 md:py-32" data-testid="brands-section">
        <div className="text-center mb-16">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-3">
            {t("home.brands")}
          </p>
          <h2
            className="text-2xl md:text-3xl"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("home.brands_subtitle")}
          </h2>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
          {brands.map((brand) => (
            <Link
              key={brand}
              to={`/shop?brand=${encodeURIComponent(brand)}`}
              className="text-lg md:text-xl tracking-wide opacity-40 hover:opacity-100 transition-opacity duration-500"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid={`brand-link-${brand.toLowerCase().replace(/\s/g, "-")}`}
            >
              {brand}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
